import { useState } from "react";
import { FeedbackModel } from "model";
import { Alert, Colors } from "swing-components";

import { translate, truncateString } from "~utils";

type SchoolFeedbackProps = {
  feedback: FeedbackModel[];
};

export function SchoolFeedback(props: SchoolFeedbackProps) {
  const { feedback } = props;

  /********* Constants ********/
  const feedbackObject = feedback[0];

  const feedbackRating =
    feedbackObject.rating === "RATING_POSITIVE"
      ? translate("MSF_CHOICE_BUTTON_POSITIVE_LABEL")
      : feedbackObject.rating === "RATING_NEUTRAL"
        ? translate("MSF_CHOICE_BUTTON_NEUTRAL_LABEL")
        : feedbackObject.rating === "RATING_NEGATIVE"
          ? translate("MSF_CHOICE_BUTTON_NEGATIVE_LABEL")
          : "";

  /********* Render ********/
  return (
    <div css={{ background: Colors.white200, display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Title */}
      <span css={{ fontSize: 20 }}>{translate("SCHOOL_FEEDBACK_LABEL")}</span>

      {/* Admin Name */}
      {feedbackObject.adminName && (
        <span>{translate("SCHOOL_FEEDBACK_SUBMITTED_BY", feedbackObject.adminName)}</span>
      )}

      {/* Rating */}
      {feedbackObject.rating && (
        <div css={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <span css={{ fontSize: 14, fontWeight: 600, lineHeight: "24px" }}>
            {translate("SCHOOL_FEEDBACK_REVIEW_RATING_LABEL")}
          </span>
          <span>{feedbackRating}</span>
        </div>
      )}

      {/* Favorited Sub */}
      {feedbackObject.rating === "RATING_POSITIVE" ||
        (feedbackObject.rating === "RATING_NEUTRAL" && (
          <div css={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <span css={{ fontSize: 14, fontWeight: 600, lineHeight: "24px" }}>
              {translate("SCHOOL_FEEDBACK_FAVORITED_SUB_LABEL")}
            </span>
            <span>{feedbackObject.preferred ? translate("LABEL_YES") : translate("LABEL_NO")}</span>
          </div>
        ))}

      {/* Blocked Sub */}
      {feedbackObject.rating === "RATING_NEGATIVE" && (
        <div css={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <span css={{ fontSize: 14, fontWeight: 600, lineHeight: "24px" }}>
            {translate("SCHOOL_FEEDBACK_BLOCKED_SUB_LABEL")}
          </span>
          <span>
            {feedbackObject.isBlocked ? (
              <>
                {translate("LABEL_YES")}{" "}
                {feedbackObject.reasons.length > 0 && (
                  <>
                    <span css={{ fontStyle: "italic" }}>
                      ({FeedbackModel.getReasonsDisplayList(feedbackObject.reasons).join(", ")})
                    </span>
                  </>
                )}
              </>
            ) : (
              translate("LABEL_NO")
            )}
          </span>
        </div>
      )}

      {/* Has Safety Issue */}
      {feedbackObject.reasons.includes("REASON_SAFETY") && (
        <Alert color="info" title={translate("SCHOOL_FEEDBACK_SAFETY_MESSAGING_TITLE")}>
          <p
            css={{ margin: 0, a: { textDecoration: "none" } }}
            dangerouslySetInnerHTML={{ __html: translate("SCHOOL_FEEDBACK_SAFETY_MESSAGING") }}
          />
        </Alert>
      )}

      {/* Comments */}
      <div css={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <span css={{ fontSize: 14, fontWeight: 600, lineHeight: "24px" }}>
          {translate("SCHOOL_FEEDBACK_NOTES_LABEL")}
        </span>
        {feedbackObject.comments ? (
          <FeedbackComments comments={feedbackObject.comments} />
        ) : (
          <span css={{ fontStyle: "italic" }}>{translate("LABEL_NONE")}</span>
        )}
      </div>
    </div>
  );
}

type FeedbackCommentsProps = {
  comments: string;
};

function FeedbackComments(props: FeedbackCommentsProps) {
  const { comments } = props;
  const TRUNCATE_COMMENTS_CUTOFF = 200;

  const [isTruncated, setIsTruncated] = useState<boolean>(
    comments.length > TRUNCATE_COMMENTS_CUTOFF,
  );

  return (
    <>
      {isTruncated ? (
        <>
          <span>{truncateString(comments, TRUNCATE_COMMENTS_CUTOFF)}</span>
          <button
            onClick={() => {
              setIsTruncated(false);
            }}
            css={{ background: "none" }}
            className="font-medium swing-icon-tooltip"
          >
            {translate("SCHOOL_FEEDBACK_READ_MORE_LABEL")}
          </button>
        </>
      ) : (
        comments
      )}
    </>
  );
}
